import { useState, useEffect } from "react";
import { useToast } from '@/hooks/use-toast';
import { 
  createDigitalContent,
  getDigitalContents,
  updateDigitalContent,
  deleteDigitalContent
} from '@/services/digitalContent/digitalContentService';
import { supabase } from '@/integrations/supabase/client';
import { 
  DigitalContent, 
  DigitalContentFormData, 
  DigitalContentCreateData, 
  DigitalContentUpdateData 
} from '@/types/digitalContent';
import { 
  getMentorshipOfferings,
  createMentorshipOffering,
  updateMentorshipOffering,
  deleteMentorshipOffering,
  canDeleteMentorshipOffering
} from '@/services/mentorOfferings/mentorOfferingsManagementService';
import { OfferingCreateData, OfferingUpdateData } from '@/types/mentorOffering';
import { MentorshipFormData } from '@/components/mentor/MentorshipOfferingForm';
import { uploadDigitalContentCover } from '@/services/imageUploadService';

// Tipo unificado para serviços
export interface UnifiedService {
  id: string;
  title: string;
  description: string;
  price_cents: number;
  type: 'mentoria' | 'conteudo';
  cover_image_url?: string | null;
  is_active: boolean;
  created_at?: string;
  // Campos específicos de mentoria
  duration_minutes?: number;
  is_group?: boolean;
  max_participants?: number;
  game?: { id: string; name: string; cover_url?: string } | null;
  // Campos específicos de conteúdo
  content_type?: string;
  file_url?: string;
}

export const useIntegratedServices = () => {
  const [services, setServices] = useState<UnifiedService[]>([]);
  const [loading, setLoading] = useState(true);
  const [isTypeSelectionOpen, setIsTypeSelectionOpen] = useState(false);
  const [isMentoriaDialogOpen, setIsMentoriaDialogOpen] = useState(false);
  const [isConteudoDialogOpen, setIsConteudoDialogOpen] = useState(false);
  const [isEditMentoriaOpen, setIsEditMentoriaOpen] = useState(false);
  const [isEditConteudoOpen, setIsEditConteudoOpen] = useState(false);
  const [editingMentoria, setEditingMentoria] = useState<MentorshipFormData | null>(null);
  const [editingConteudo, setEditingConteudo] = useState<DigitalContent | null>(null);
  const [currentMentorId, setCurrentMentorId] = useState<string | null>(null);
  const { toast } = useToast();

  // Get current mentor ID
  useEffect(() => {
    const fetchMentorInfo = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          const { data: mentorData, error } = await supabase
            .from("mentor_info")
            .select("id")
            .eq("profile_id", session.user.id)
            .single();
            
          if (error) throw error;
          setCurrentMentorId(mentorData?.id || null);
        }
      } catch (error) {
        console.error("Error fetching mentor info:", error);
      }
    };
    
    fetchMentorInfo();
  }, []);

  // Fetch all services when mentor ID is available
  useEffect(() => {
    if (currentMentorId) {
      fetchAllServices();
    }
  }, [currentMentorId]);

  const fetchAllServices = async () => {
    if (!currentMentorId) return;
    
    setLoading(true);
    try {
      console.log('🔄 [fetchAllServices] Buscando serviços atualizados...');
      
      // Buscar mentorias e conteúdos em paralelo
      const [mentoriasData, conteudosData] = await Promise.all([
        getMentorshipOfferings(),
        getDigitalContents()
      ]);
      
      console.log('📋 [fetchAllServices] Mentorias encontradas:', mentoriasData.length);
      console.log('📋 [fetchAllServices] Conteúdos encontrados:', conteudosData.length);
      
      // Converter mentorias para formato unificado
      const mentoriasUnified: UnifiedService[] = mentoriasData.map(m => ({
        id: m.id,
        title: m.title,
        description: m.description,
        price_cents: m.price_cents,
        type: 'mentoria' as const,
        cover_image_url: m.cover_image_url,
        is_active: m.is_active,
        created_at: m.created_at,
        duration_minutes: m.duration_minutes,
        is_group: m.is_group,
        max_participants: m.max_participants,
        game: m.game
      }));
      
      // Converter conteúdos para formato unificado
      const conteudosUnified: UnifiedService[] = conteudosData.map(c => {
        console.log(`📄 [fetchAllServices] Conteúdo "${c.title}" - Capa:`, c.cover_image_url);
        return {
          id: c.id,
          title: c.title,
          description: c.description,
          price_cents: c.price_cents,
          type: 'conteudo' as const,
          cover_image_url: c.cover_image_url,
          is_active: c.is_active,
          created_at: c.created_at,
          content_type: c.content_type,
          file_url: c.file_url
        };
      });
      
      // Combinar e ordenar por data de criação
      const allServices = [...mentoriasUnified, ...conteudosUnified]
        .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime());
      
      console.log('✅ [fetchAllServices] Total de serviços carregados:', allServices.length);
      setServices(allServices);
    } catch (error) {
      console.error("Error fetching services:", error);
      toast({
        title: "Erro",
        description: "Erro ao carregar serviços",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Handlers para os modais
  const handleCreateClick = () => {
    setIsTypeSelectionOpen(true);
  };

  const handleSelectMentoria = () => {
    setIsTypeSelectionOpen(false);
    setIsMentoriaDialogOpen(true);
  };

  const handleSelectConteudo = () => {
    setIsTypeSelectionOpen(false);
    setIsConteudoDialogOpen(true);
  };

  // Handler para criar mentoria
  const handleCreateMentoria = async (formData: MentorshipFormData, coverFile?: File | null) => {
    if (!currentMentorId) return;
    
    try {
      const offeringData: OfferingCreateData = {
        title: formData.title,
        description: formData.description,
        price_cents: Math.round(parseFloat(formData.price.replace(',', '.')) * 100),
        duration_minutes: parseInt(formData.duration, 10),
        game_id: formData.gameId?.trim() === '' ? null : formData.gameId || null,
        cover_image_url: null as string | null,
        is_group: formData.isGroup,
        max_participants: parseInt(formData.maxParticipants, 10)
      };

      // Handle cover image upload if provided
      if (coverFile) {
        const sanitizedTitle = formData.title.replace(/[^a-zA-Z0-9]/g, '_');
        const fileExtension = coverFile.name.split('.').pop();
        const fileName = `${currentMentorId}/${sanitizedTitle}-${Date.now()}.${fileExtension}`;
        
        const { data, error } = await supabase.storage
          .from("mentorship-covers")
          .upload(fileName, coverFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) {
          throw new Error(`Failed to upload cover image: ${error.message}`);
        }

        offeringData.cover_image_url = supabase.storage.from("mentorship-covers").getPublicUrl(data.path).data.publicUrl;
      }

      await createMentorshipOffering(offeringData);
      
      toast({
        title: "Sucesso",
        description: "Mentoria criada com sucesso!"
      });
      
      setIsMentoriaDialogOpen(false);
      fetchAllServices();
    } catch (error: any) {
      console.error("Error creating mentoria:", error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao criar mentoria",
        variant: "destructive"
      });
    }
  };

  // Handler para criar conteúdo
  const handleCreateConteudo = async (formData: DigitalContentFormData & { coverImageFile?: File }) => {
    try {
      let coverImageUrl: string | null = null;
      
      // Upload da imagem de capa se fornecida
      if (formData.coverImageFile) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('Usuário não autenticado');
        }
        
        const uploadResult = await uploadDigitalContentCover(formData.coverImageFile, user.id);
        
        if (!uploadResult.success) {
          throw new Error(uploadResult.error || 'Erro no upload da imagem');
        }
        
        coverImageUrl = uploadResult.url || null;
      }
      
      const createData: DigitalContentCreateData = {
        title: formData.title,
        description: formData.description,
        content_type: formData.content_type,
        price_cents: formData.price_cents,
        cover_image_url: coverImageUrl,
        file_url: formData.file_url,
        file_size_bytes: 0,
        file_name: formData.file_url.split('/').pop() || formData.title,
        preview_content: null,
        tags: [],
        difficulty_level: null,
        estimated_duration_minutes: null,
        is_featured: false,
        game_id: null,
      };

      await createDigitalContent(createData);
      
      toast({
        title: "Sucesso!",
        description: "Conteúdo criado com sucesso",
      });

      setIsConteudoDialogOpen(false);
      fetchAllServices();
    } catch (error) {
      console.error('Erro ao criar conteúdo:', error);
      toast({
        title: "Erro",
        description: error instanceof Error ? error.message : "Erro ao criar conteúdo",
        variant: "destructive"
      });
    }
  };

  // Handler para deletar serviço
  const handleDeleteService = async (id: string, type: 'mentoria' | 'conteudo') => {
    try {
      if (type === 'mentoria') {
        // Verificar se pode excluir antes de mostrar confirmação
        const { canDelete, reason, sessionsCount } = await canDeleteMentorshipOffering(id);
        
        let confirmMessage = "Tem certeza que deseja excluir esta mentoria? Esta ação não pode ser desfeita.";
        
        if (!canDelete) {
          confirmMessage = `⚠️ ATENÇÃO!\n\n${reason}\n\nA mentoria será desativada em vez de excluída permanentemente.\n\nDeseja continuar?`;
        }
        
        if (!confirm(confirmMessage)) {
          return;
        }

        await deleteMentorshipOffering(id);
        
        const successMessage = canDelete 
          ? "Mentoria excluída com sucesso!" 
          : "Mentoria desativada com sucesso! Ela não aparecerá mais para novos agendamentos, mas as sessões já marcadas permanecerão válidas.";
        
        toast({
          title: "Sucesso",
          description: successMessage,
        });
      } else {
        if (!confirm("Tem certeza que deseja excluir este conteúdo? Esta ação não pode ser desfeita.")) {
          return;
        }
        
        await deleteDigitalContent(id);
        
        toast({
          title: "Sucesso",
          description: "Conteúdo excluído com sucesso!",
        });
      }
      
      fetchAllServices();
    } catch (error) {
      console.error("Error deleting service:", error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao processar a solicitação.",
        variant: "destructive"
      });
    }
  };

  // Handler para editar serviço
  const handleEditService = (service: UnifiedService) => {
    if (service.type === 'mentoria') {
      // Converter para formato de mentoria e armazenar o ID
      const mentoriaData: MentorshipFormData & { id: string } = {
        id: service.id, // Adicionar o ID para facilitar a atualização
        title: service.title,
        description: service.description,
        price: (service.price_cents / 100).toFixed(2).replace('.', ','),
        duration: service.duration_minutes?.toString() || '60',
        gameId: service.game?.id || '',
        isGroup: service.is_group || false,
        isActive: service.is_active ?? true,
        maxParticipants: service.max_participants?.toString() || '1'
      };
      
      setEditingMentoria(mentoriaData as any);
      setIsEditMentoriaOpen(true);
    } else {
      // Converter para formato de conteúdo
      const conteudoData = {
        id: service.id,
        title: service.title,
        description: service.description,
        price_cents: service.price_cents,
        content_type: (service.content_type || 'pdf') as any,
        file_url: service.file_url || '',
        cover_image_url: service.cover_image_url,
        is_active: service.is_active,
        created_at: service.created_at || '',
        updated_at: service.created_at || '',
        mentor_id: currentMentorId || ''
      };
      
      setEditingConteudo(conteudoData as any);
      setIsEditConteudoOpen(true);
    }
  };

  // Handler para atualizar mentoria
  const handleUpdateMentoria = async (formData: MentorshipFormData, coverFile?: File | null) => {
    if (!editingMentoria || !currentMentorId) return;
    
    try {
      const updateData: OfferingUpdateData = {
        title: formData.title,
        description: formData.description,
        price_cents: Math.round(parseFloat(formData.price.replace(',', '.')) * 100),
        duration_minutes: parseInt(formData.duration, 10),
        game_id: formData.gameId?.trim() === '' ? null : formData.gameId || null,
        is_group: formData.isGroup,
        max_participants: parseInt(formData.maxParticipants, 10)
      };

      // Handle cover image upload if provided
      if (coverFile) {
        const sanitizedTitle = formData.title.replace(/[^a-zA-Z0-9]/g, '_');
        const fileExtension = coverFile.name.split('.').pop();
        const fileName = `${currentMentorId}/${sanitizedTitle}-${Date.now()}.${fileExtension}`;
        
        const { data, error } = await supabase.storage
          .from("mentorship-covers")
          .upload(fileName, coverFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) {
          throw new Error(`Failed to upload cover image: ${error.message}`);
        }

        updateData.cover_image_url = supabase.storage.from("mentorship-covers").getPublicUrl(data.path).data.publicUrl;
      }

      // Usar o ID armazenado no editingMentoria
      const mentoriaId = (editingMentoria as any).id;
      if (!mentoriaId) throw new Error('ID da mentoria não encontrado');

      await updateMentorshipOffering(mentoriaId, updateData);
      
      toast({
        title: "Sucesso",
        description: "Mentoria atualizada com sucesso",
      });
      
      setIsEditMentoriaOpen(false);
      setEditingMentoria(null);
      await fetchAllServices();
    } catch (error) {
      console.error("Error updating mentoria:", error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar mentoria",
        variant: "destructive"
      });
    }
  };

  // Handler para atualizar conteúdo
  const handleUpdateConteudo = async (formData: DigitalContentFormData & { coverImageFile?: File }) => {
    if (!editingConteudo) return;
    
    try {
      let coverImageUrl = editingConteudo.cover_image_url;

      // Handle cover image upload if provided
      if (formData.coverImageFile) {
        console.log('📤 [handleUpdateConteudo] Fazendo upload da nova capa...');
        
        const uploadResult = await uploadDigitalContentCover(formData.coverImageFile, editingConteudo.id);
        
        if (!uploadResult.success) {
          throw new Error(uploadResult.error || 'Erro no upload da imagem');
        }
        
        coverImageUrl = uploadResult.url || null;
        console.log('✅ [handleUpdateConteudo] Nova capa carregada:', coverImageUrl);
      }

      const updateData: DigitalContentUpdateData = {
        title: formData.title,
        description: formData.description,
        content_type: formData.content_type,
        file_url: formData.file_url,
        price_cents: formData.price_cents,
        cover_image_url: coverImageUrl
      };

      console.log('💾 [handleUpdateConteudo] Atualizando conteúdo com dados:', updateData);

      await updateDigitalContent(editingConteudo.id, updateData);
      
      console.log('✅ [handleUpdateConteudo] Conteúdo atualizado no banco de dados');
      
      toast({
        title: "Sucesso",
        description: "Conteúdo atualizado com sucesso",
      });
      
      setIsEditConteudoOpen(false);
      setEditingConteudo(null);
      
      // Forçar atualização da lista
      console.log('🔄 [handleUpdateConteudo] Atualizando lista de serviços...');
      await fetchAllServices();
      console.log('✅ [handleUpdateConteudo] Lista atualizada');
    } catch (error) {
      console.error("Error updating content:", error);
      toast({
        title: "Erro",
        description: error instanceof Error ? error.message : "Erro ao atualizar conteúdo",
        variant: "destructive"
      });
    }
  };

  // Handler para alternar status ativo/inativo
  const handleToggleActive = async (id: string, isActive: boolean, type: 'mentoria' | 'conteudo') => {
    try {
      console.log(`🔄 [handleToggleActive] Alternando status: ${type} ${id} para ${isActive ? 'ativo' : 'inativo'}`);
      
      if (type === 'mentoria') {
        const result = await updateMentorshipOffering(id, { is_active: isActive });
        console.log(`✅ [handleToggleActive] Mentoria atualizada:`, result);
        
        toast({
          title: "Sucesso",
          description: `Mentoria ${isActive ? 'ativada' : 'desativada'} com sucesso!`,
        });
      } else {
        const result = await updateDigitalContent(id, { is_active: isActive });
        console.log(`✅ [handleToggleActive] Conteúdo atualizado:`, result);
        
        toast({
          title: "Sucesso",
          description: `Conteúdo ${isActive ? 'ativado' : 'desativado'} com sucesso!`,
        });
      }
      
      // Atualizar a lista de serviços
      console.log(`🔄 [handleToggleActive] Atualizando lista de serviços...`);
      await fetchAllServices();
      console.log(`✅ [handleToggleActive] Lista de serviços atualizada`);
    } catch (error: any) {
      console.error("❌ [handleToggleActive] Erro ao alternar status:", error);
      console.error("❌ [handleToggleActive] Detalhes do erro:", {
        message: error?.message,
        code: error?.code,
        details: error?.details,
        hint: error?.hint
      });
      
      toast({
        title: "Erro",
        description: error?.message || "Ocorreu um erro ao alterar o status do serviço.",
        variant: "destructive"
      });
    }
  };

  return {
    services,
    loading,
    isTypeSelectionOpen,
    setIsTypeSelectionOpen,
    isMentoriaDialogOpen,
    setIsMentoriaDialogOpen,
    isConteudoDialogOpen,
    setIsConteudoDialogOpen,
    isEditMentoriaOpen,
    setIsEditMentoriaOpen,
    isEditConteudoOpen,
    setIsEditConteudoOpen,
    editingMentoria,
    editingConteudo,
    handleCreateClick,
    handleSelectMentoria,
    handleSelectConteudo,
    handleCreateMentoria,
    handleCreateConteudo,
    handleDeleteService,
    handleEditService,
    handleUpdateMentoria,
    handleUpdateConteudo,
    handleToggleActive,
    fetchAllServices
  };
}; 